'use client';

import { m, useScroll, useTransform, type Variants } from 'motion/react';
import { useEffect, useRef, type MouseEvent } from 'react';
import SocialLinks from './SocialLinks';
import BrandWordmark from './BrandWordmark';

export default function Hero() {
    const videoRef = useRef<HTMLVideoElement>(null);
    const sectionRef = useRef<HTMLDivElement>(null);
    const { scrollY } = useScroll();
    const heroDarkness = useTransform(scrollY, (latestScroll) => {
        const viewportHeight = typeof window === 'undefined' ? 900 : window.innerHeight;
        const progress = Math.min(1, Math.max(0, latestScroll / (viewportHeight * 0.92)));
        return Math.pow(progress, 0.72);
    });
    const scrollIndicatorOpacity = useTransform(scrollY, (latestScroll) => {
        const viewportHeight = typeof window === 'undefined' ? 900 : window.innerHeight;
        return 1 - Math.min(1, Math.max(0, latestScroll / (viewportHeight * 0.2)));
    });

    useEffect(() => {
        const video = videoRef.current;
        const section = sectionRef.current;
        if (!video || !section) return;

        // Set slower playback for ambient effect
        video.playbackRate = 0.8;

        // Prevent Picture-in-Picture
        const handleEnterPictureInPicture = (e: Event) => {
            e.preventDefault();
        };

        const handleContextMenu = (e: Event) => {
            e.preventDefault();
        };

        video.addEventListener('enterpictureinpicture', handleEnterPictureInPicture);
        video.addEventListener('contextmenu', handleContextMenu);

        let isHeroVisible = true;
        let isHeroCovered = false;
        let isPlaybackActive: boolean | null = null;
        let playbackFrame: number | null = null;

        const syncPlayback = (isVisible: boolean) => {
            const isCoveredByContent = window.scrollY >= window.innerHeight * 0.92;
            if (isCoveredByContent !== isHeroCovered) {
                isHeroCovered = isCoveredByContent;
                section.classList.toggle('hero-covered', isCoveredByContent);
            }

            const shouldPlay = isVisible && !isCoveredByContent && !document.hidden;
            if (shouldPlay === isPlaybackActive) return;

            isPlaybackActive = shouldPlay;
            section.classList.toggle('section-effects-paused', !shouldPlay);
            if (shouldPlay) {
                void video.play().catch(() => {
                    // Autoplay can be declined by browser policy; the poster/first
                    // frame remains a valid background in that case.
                });
            } else {
                video.pause();
            }
        };

        const observer = new IntersectionObserver(
            ([entry]) => {
                isHeroVisible = entry.isIntersecting && entry.intersectionRatio >= 0.05;
                syncPlayback(isHeroVisible);
            },
            { threshold: [0, 0.05] },
        );
        observer.observe(section);

        const handleScroll = () => {
            if (playbackFrame !== null) return;
            playbackFrame = window.requestAnimationFrame(() => {
                playbackFrame = null;
                syncPlayback(isHeroVisible);
            });
        };
        window.addEventListener('scroll', handleScroll, { passive: true });

        const handleVisibilityChange = () => {
            syncPlayback(isHeroVisible);
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            observer.disconnect();
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (playbackFrame !== null) window.cancelAnimationFrame(playbackFrame);
            section.classList.remove('section-effects-paused');
            section.classList.remove('hero-covered');
            video.pause();
            video.removeEventListener('enterpictureinpicture', handleEnterPictureInPicture);
            video.removeEventListener('contextmenu', handleContextMenu);
        };
    }, []);

    const scrollToSection = (event: MouseEvent<HTMLAnchorElement>, sectionId: string) => {
        event.preventDefault();
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const containerVariants: Variants = {
        hidden: {},
        visible: {
            transition: {
                staggerChildren: 0.12,
            },
        },
    };

    const itemVariants: Variants = {
        hidden: { y: 18 },
        visible: {
            y: 0,
            transition: {
                duration: 0.55,
                ease: "easeOut",
            },
        },
    };

    return (
        <section id="hero" ref={sectionRef} className="sticky top-0 z-0 h-[100svh] min-h-[100dvh] w-full max-w-[100vw] overflow-hidden">
            {/* Video Background */}
            <div className="absolute inset-0 z-0">
                <m.video
                    ref={videoRef}
                    loop
                    muted
                    playsInline
                    disablePictureInPicture
                    className="h-full w-full object-cover"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                >
                    <source src="/hero-video.mp4" type="video/mp4" />
                </m.video>

                {/* Video Overlay */}
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(8,5,20,0.16),rgba(2,1,7,0.78)_72%)]" />
                <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(3,2,8,0.22),transparent_35%,rgba(3,2,8,0.88))]" />
            </div>

            {/* Content */}
            <m.div
                className="relative z-10 flex h-full items-center justify-center"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                <div className="section-padding mx-auto flex max-w-5xl flex-col items-center text-center">
                    <m.div variants={itemVariants} className="section-eyebrow mb-7">
                        Live looping · Piano · Production
                    </m.div>
                    <m.h1
                        variants={itemVariants}
                        className="hero-title mb-9 text-6xl sm:text-7xl md:text-8xl lg:text-[7.6rem]"
                    >
                        <BrandWordmark />
                    </m.h1>

                    <m.div variants={itemVariants} className="hero-laser mb-8" aria-hidden />

                    <m.div variants={itemVariants} className="mb-8 flex flex-wrap items-center justify-center gap-3">
                        <a href="#media" className="neon-button neon-button-primary animated-gradient-button" onClick={(event) => scrollToSection(event, 'media')}>
                            Watch performances
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="m9 18 6-6-6-6" />
                            </svg>
                        </a>
                        <a href="#gear" className="neon-button" onClick={(event) => scrollToSection(event, 'gear')}>
                            Explore the setup
                        </a>
                    </m.div>

                    <m.div variants={itemVariants} className="opacity-85">
                        <SocialLinks />
                    </m.div>
                </div>
            </m.div>

            {/* The following section progressively shades this fixed scene as it passes over it. */}
            <m.div
                className="pointer-events-none absolute inset-0 z-30 bg-[#020106]"
                style={{ opacity: heroDarkness }}
                aria-hidden
            />

            {/* Scroll Indicator */}
            <m.div
                className="absolute bottom-28 left-0 right-0 z-20 flex justify-center sm:bottom-24"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2, duration: 1 }}
                style={{ opacity: scrollIndicatorOpacity }}
            >
                <div className="section-padding text-center">
                    {/* Scroll to explore text */}
                    <m.p
                        className="mb-3 font-display text-[10px] font-semibold uppercase tracking-[0.24em] text-white/45"
                        initial={{ opacity: 0 }}
                        animate={{
                            opacity: 1,
                            y: [0, -10, 0]
                        }}
                        transition={{
                            opacity: { delay: 2.5, duration: 0.8 },
                            y: { repeat: Infinity, duration: 2, delay: 2.5 }
                        }}
                    >
                        Explore
                    </m.p>

                    {/* Scroll wheel */}
                    <m.div
                        className="mx-auto flex h-9 w-5 justify-center rounded-full border border-white/25"
                        animate={{ y: [0, -10, 0] }}
                        transition={{ repeat: Infinity, duration: 2, delay: 2.5 }}
                    >
                        <m.div
                            className="mt-2 h-2 w-0.5 rounded-full bg-cyan-300 shadow-[0_0_8px_#38dcff]"
                            animate={{ y: [0, 15, 0] }}
                            transition={{ repeat: Infinity, duration: 2, delay: 2.5 }}
                        />
                    </m.div>
                </div>
            </m.div>
        </section>
    );
}
